/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

/**
 * `useOutsideMousedown` hook for capturing mousedown outside of an element
 */
import type { RefObject } from 'preact';
import { useEffect } from 'preact/hooks';
import { coerceArray } from '../../utils/UNSAFE_arrayUtils';
import { isLogicalAncestor } from '#utils/UNSAFE_popupUtils';

type Props = {
  /**
   * If true outside mousedown is disabled
   */
  isDisabled?: boolean;
  /**
   * Reference to element or array of elements relative to which outside mousedown is performed
   */
  ref: RefObject<HTMLElement> | RefObject<HTMLElement>[];
  /**
   * Outside mousedown callback
   */
  handler?: (event: MouseEvent) => void;
};

const isPointOutside = (event: Event, ref: RefObject<HTMLElement>[]) => {
  const target = event.target as HTMLElement;
  if (target) {
    const doc = getDocument(target);
    if (!doc.contains(target)) return false;
  }
  return ref.every(
    (elRef) =>
      !elRef.current?.contains(target) && !isLogicalAncestor(elRef.current as HTMLElement, target)
  );
};

const getDocument = (node?: Node): Document => node?.ownerDocument ?? document;

export const useOutsideMousedown = ({ isDisabled: disabled = false, ref, handler }: Props) => {
  useEffect(() => {
    const refs = coerceArray(ref);
    if (
      !handler ||
      disabled ||
      (refs.length > 0 && refs.findIndex((elRef) => elRef.current === null) > -1)
    )
      return;

    const onMouseDown = (event: MouseEvent) => {
      if (isPointOutside(event, refs)) {
        handler?.(event);
      }
    };
    const firstElRef = refs[0];
    const doc = getDocument(firstElRef.current!);
    if (doc) {
      doc.addEventListener('mousedown', onMouseDown, true);

      return () => {
        doc.removeEventListener('mousedown', onMouseDown, true);
      };
    }
    return undefined;
  }, [disabled, handler, ref]);
};
